import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useMemo, useEffect, useState, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import { Button, Chip, MenuItem } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
// routes
import { useSnackbar } from 'src/components/snackbar';
// hooks
import { useResponsive } from 'src/hooks/use-responsive';
// components
import FormProvider, {
    RHFAutocomplete,
    RHFSelect,
    RHFTextField,
    RHFUploadAvatar,
} from 'src/components/hook-form';
import { fData } from 'src/utils/format-number';
import { useRegisterConsultantMutation, useUpdateConsultantByIdMutation } from 'src/services/user/consultant-api';
import { useRouter } from 'src/routes/hooks';
import { paths } from 'src/routes/paths';
import { getFileExtension, getModifiedMobileNumber, validateNIC, validatePhoneNumber } from 'src/utils/custom-function';
import { useGetUploadURLMutation } from 'src/services/profile/profille-api';
import { useUploadToS3Mutation } from 'src/services/base-api';
import { formatDate } from 'src/utils/format-time';
// ----------------------------------------------------------------------

const designationList = ["CONSULTANT", "SALES"];

const accessList = ['MAP'];

const MAP_ROLE = {
    CONSULTANT: 'TOUR_CONSULTANT',
    SALES: 'SALES',
};

export default function QuickStaffForm({ consultant, open, onClose, onSuccess }) {
    const mdUp = useResponsive('up', 'md');

    const router = useRouter();

    const { enqueueSnackbar } = useSnackbar();

    const [imageFile, setImageFile] = useState(null);

    const NewProductSchema = Yup.object().shape({
        fname: Yup.string().required('First name cannot be empty or invalid'),
        lname: Yup.string().required('Last name cannot be empty or invalid'),
        email: Yup.string().required('Email is required').email('Email must be a valid email address'),
        contactNumber: Yup.string().required('Contact number cannot be empty').test('isValidContactNumber', 'Invalid contact number', () => {
            const isValid = validatePhoneNumber(values.contactNumber);
            return isValid;
        }),
        role: Yup.string().required('Role cannot be empty'),
        access:  Yup.array()
    });

    const [updateConsultant, { isLoading: isLoadingUpdateConsultant, isSuccess: isSuccessUpdateConsultant, error: errorUpdateConsultant }] = useUpdateConsultantByIdMutation();

    const [createConsultant, { isLoading: isLoadingCreateConsultant, isSuccess: isSuccessCreateConsultant, error: errorCreateConsultant }] = useRegisterConsultantMutation();

    const [getUploadURL, { isLoading: isLoadingUploadURLData, error: getUploadURLError, data: getUploadURLData, isSuccess: getUploadURLSuccess }] = useGetUploadURLMutation();

    const [uploadToS3, { isLoading: isLoadingUploadToS3, error: uploadToS3Error, isSuccess: uploadToS3Success }] = useUploadToS3Mutation();

    const getModifiedRole = useCallback((role) => {
        if(role === 'TOUR_CONSULTANT'){
            return 'CONSULTANT';
        }
        
        return role;
    }, []);

    const defaultValues = useMemo(
        () => ({
            photoURL: consultant?.profile_img || null,
            fname: consultant?.first_name || '',
            lname: consultant?.last_name || '',
            email: consultant?.email || '',
            contactNumber: consultant?.phone_no || '',
            role: consultant ? getModifiedRole(consultant.role) : '',
            access: consultant?.enabled_features || []
        }),
        [consultant, getModifiedRole]
    );

    const methods = useForm({
        resolver: yupResolver(NewProductSchema),
        defaultValues,
    });

    const {
        setValue,
        reset,
        watch,
        handleSubmit,
        setError,
        formState: { isSubmitting, },
    } = methods;

    const values = watch();

    const [inSubmission, setInSubmission] = useState(false);

    const submittedValuesRef = useRef(null);

    const onSubmit = handleSubmit(async (data) => {
        setInSubmission(true);

        console.log(data);

        const selectedAccessList = values.role === "SALES" ? [] : values.access;
        const backendRole = MAP_ROLE[values.role] || values.role;

        submittedValuesRef.current = {
            fname: values.fname,
            lname: values.lname,
            email: values.email,
            contactNumber: values.contactNumber,
            role: values.role,
            access: values.access,
            photoURL: values.photoURL,
            backendRole,
            selectedAccessList,
        };

        try {
            if (imageFile) {
                const fileExtension = getFileExtension(data.photoURL);
                await getUploadURL({ extension: fileExtension, type: "PROFILE_IMG" });
            }
            else if (consultant) {
                const body = {
                    phoneNo: getModifiedMobileNumber(data.contactNumber),
                    displayName: values.fname,
                    firstName: values.fname,
                    lastName: values.lname,
                    role: backendRole,
                    enabledFeatures: selectedAccessList,
                };

                if (values.photoURL) {
                    body.profileImg = values.photoURL;
                }
                
                console.log(body);
                await updateConsultant({ id: consultant.id, body });
            }
            else {
                const body = {
                    'email': values.email,
                    'display_name': values.fname,
                    'first_name': values.fname,
                    'last_name': values.lname,
                    'phone_no': getModifiedMobileNumber(values.contactNumber),
                    'role': backendRole,
                    'enabled_features': selectedAccessList
                };

                console.log(body);
                await createConsultant(body);
            }
        } catch (error) {
            console.error(error);
            setInSubmission(false);
        }
    });

    useEffect(() => {
        if (getUploadURLSuccess && imageFile) {
            uploadToS3({ uploadUrl: getUploadURLData?.data?.uploadUrl, file: imageFile });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getUploadURLSuccess])

    useEffect(() => {
        if (uploadToS3Success && getUploadURLData) {
            setInSubmission(true);

            const sv = submittedValuesRef.current;
            if (!sv) return;

            const selectedAccessList = sv.role === "SALES" ? [] : sv.access;
            const backendRole = MAP_ROLE[sv.role] || sv.role;

            if (consultant){
                const body = {
                    phoneNo: getModifiedMobileNumber(sv.contactNumber),
                    profileImg: getUploadURLData?.data?.downloadUrl,
                    displayName: sv.fname,
                    firstName: sv.fname,
                    lastName: sv.lname,
                    role: backendRole,
                    enabledFeatures: selectedAccessList,
                }

                console.log(body);
                updateConsultant({ id: consultant.id, body });
            }
            else{
                const body = {
                    'email': sv.email,
                    'display_name': sv.fname,
                    'first_name': sv.fname,
                    'last_name': sv.lname,
                    'phone_no': getModifiedMobileNumber(sv.contactNumber),
                    'profile_img': getUploadURLData?.data?.downloadUrl,
                    'role': backendRole,
                    'enabled_features': selectedAccessList
                };
    
                console.log(body);
                createConsultant(body);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [uploadToS3Success])

    useEffect(() => {
        if (getUploadURLError || uploadToS3Error) {
            setInSubmission(false);
            enqueueSnackbar('Image upload Failed!', { variant: 'error' });
        }
    }, [enqueueSnackbar, getUploadURLError, uploadToS3Error]);

    useEffect(() => {
        const currentURL = window.location.href;
        const parts = currentURL.split("/");
        const value = parts[parts.length - 1];
        if (value === 'edit') {
            if (!consultant) {
                router.push(paths.dashboard.user.root);
            }
        }
    }, [router, consultant]);

    const handleDrop = useCallback(
        (acceptedFiles) => {
            const file = acceptedFiles[0];

            const newFile = Object.assign(file, {
                preview: URL.createObjectURL(file),
            });

            if (file) {
                setValue('photoURL', newFile, { shouldValidate: true });
                setImageFile(file);
            }
        },
        [setValue]
    );

    useEffect(() => {
        if (consultant) {
            reset(defaultValues);
        }
    }, [consultant, defaultValues, reset]);

    useEffect(() => {
        setInSubmission(false);

        if (isSuccessUpdateConsultant || isSuccessCreateConsultant) {
            reset();
            enqueueSnackbar(consultant ? 'Update success!' : 'Create success!');
            onClose();
            onSuccess();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccessCreateConsultant, isSuccessUpdateConsultant])

    useEffect(() => {
        setInSubmission(false);

        if (errorCreateConsultant) {
            enqueueSnackbar(errorCreateConsultant.data?.message, { variant: 'error' });
        }
        else if (errorUpdateConsultant) {
            enqueueSnackbar(errorUpdateConsultant.data?.message, { variant: 'error' });
        }
    }, [enqueueSnackbar, errorCreateConsultant, errorUpdateConsultant]);

    useEffect(() => {
        if (!open) {
            submittedValuesRef.current = null;
        }
    }, [open]);

    const renderBasicInfo = (
        <>
            {mdUp && (
                <Grid md={4}>
                    <Typography variant="h6" sx={{ mb: 0.5 }}>
                        Basic Info
                    </Typography>
                </Grid>
            )}

            <Grid xs={12} md={8}>
                <Card>
                    {!mdUp && <CardHeader title="Basic Info" />}

                    <Stack spacing={3} sx={{ p: 3 }}>
                        <RHFUploadAvatar
                            name="photoURL"
                            maxSize={3145728}
                            onDrop={handleDrop}
                            loading={isLoadingUploadURLData || isLoadingUploadToS3}
                            helperText={
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 3,
                                        mx: 'auto',
                                        display: 'block',
                                        textAlign: 'center',
                                        color: 'text.disabled',
                                    }}
                                >
                                    Allowed *.jpeg, *.jpg, *.png, *.gif
                                    <br /> max size of {fData(3145728)}
                                </Typography>
                            }
                        />

                        <RHFTextField name="fname" label="First Name *" />
                        <RHFTextField name="lname" label="Last Name *" />
                        <RHFTextField name="email" label="Email *" disabled={consultant}/>
                        <RHFTextField name="contactNumber" label="Contact Number *" disabled={consultant}/>

                        <RHFSelect
                            fullWidth
                            name="role"
                            label="Role *"
                            InputLabelProps={{ shrink: true }}
                            PaperPropsSx={{ textTransform: 'capitalize' }}
                            disabled={consultant}
                        >
                            {designationList.map((option) => (
                                <MenuItem key={option} value={option}>
                                    {option}
                                </MenuItem>
                            ))}
                        </RHFSelect>

                        {
                            values.role === "CONSULTANT"
                            ?
                            <RHFAutocomplete
                                name="access"
                                placeholder="+ Separate Access"
                                multiple
                                disableCloseOnSelect
                                options={accessList.map((option) => option)}
                                getOptionLabel={(option) => option}
                                renderOption={(props, option) => (
                                    <li {...props} key={option}>
                                        {option}
                                    </li>
                                )}
                                renderTags={(selected, getTagProps) =>
                                selected.map((option, index) => (
                                    <Chip
                                        {...getTagProps({ index })}
                                        key={option}
                                        label={option}
                                        size="small"
                                        color="info"
                                        variant="soft"
                                    />
                                ))
                                }
                            />
                            :
                            <></>
                        }
                    </Stack>
                </Card>
            </Grid>
        </>
    );

    return (
        <Dialog
            fullWidth
            maxWidth={false}
            open={open}
            onClose={() => {
                reset();
                submittedValuesRef.current = null;
                onClose();
            }}
            PaperProps={{
                sx: { maxWidth: 900 }
            }}
        >
            <DialogTitle>
                <Typography variant="h6" gutterBottom>
                    {
                        consultant ? "Edit" : "Create"
                    }
                </Typography>
            </DialogTitle>

            <FormProvider methods={methods} onSubmit={onSubmit}>
                <Grid container spacing={3} sx={{px: 3}}>
                    {renderBasicInfo}
                </Grid>

                <DialogActions>
                    <Button variant="outlined" onClick={() => {
                        submittedValuesRef.current = null;
                        onClose();
                        reset();
                    }}
                    >
                        Close
                    </Button>

                    <LoadingButton 
                        type="submit" 
                        variant="contained" 
                        size="medium" 
                        loading={isLoadingUpdateConsultant || isLoadingCreateConsultant || isLoadingUploadURLData || isLoadingUploadToS3}
                    >
                        {!consultant ? 'Create' : 'Update'}
                    </LoadingButton>
                </DialogActions>
            </FormProvider>
        </Dialog>
    );
}

QuickStaffForm.propTypes = {
    consultant: PropTypes.object,
    open: PropTypes.func, 
    onClose: PropTypes.func, 
    onSuccess: PropTypes.func
};
